import api from '../../../services/api';
import {
    Game,
    Team,
    Player,
    AtBat,
    Pitch,
    PitchResult,
    Play,
    Inning,
    BaseRunners,
    BaserunnerEvent,
    BatterScoutingProfile,
    GamePitcherWithPlayer,
    OpponentLineupPlayer,
    OpponentPitcherProfile,
    OpponentRosterPlayer,
} from '../../../types';

interface GameState {
    game: Game;
    homeTeam: Team;
    awayTeam: Team;
    currentPitcher?: Player;
    currentBatter?: Player;
}

export const gamesApi = {
    getAllGames: async (): Promise<Game[]> => {
        const response = await api.get<{ games: Game[] }>('/games/my-games');
        return response.data.games;
    },

    getGameById: async (id: string): Promise<Game> => {
        const response = await api.get<{ game: Game }>(`/games/${id}`);
        return response.data.game;
    },

    getGamesByTeam: async (teamId: string): Promise<Game[]> => {
        const response = await api.get<{ games: Game[] }>(`/games/team/${teamId}`);
        return response.data.games;
    },

    createGame: async (gameData: Partial<Game>): Promise<Game> => {
        const response = await api.post<{ game: Game }>('/games', gameData);
        return response.data.game;
    },

    updateGame: async (id: string, gameData: Partial<Game>): Promise<Game> => {
        const response = await api.put<{ game: Game }>(`/games/${id}`, gameData);
        return response.data.game;
    },

    deleteGame: async (id: string): Promise<void> => {
        await api.delete(`/games/${id}`);
    },

    startGame: async (id: string): Promise<Game> => {
        const response = await api.post<{ game: Game }>(`/games/${id}/start`);
        return response.data.game;
    },

    endGame: async (id: string, finalData: { home_score: number; away_score: number }): Promise<Game> => {
        const response = await api.post<{ game: Game }>(`/games/${id}/end`, finalData);
        return response.data.game;
    },

    resumeGame: async (id: string): Promise<Game> => {
        const response = await api.post<{ game: Game }>(`/games/${id}/resume`);
        return response.data.game;
    },

    advanceInning: async (id: string): Promise<Game> => {
        const response = await api.post<{ game: Game }>(`/games/${id}/advance-inning`);
        return response.data.game;
    },

    updateScore: async (id: string, homeScore: number, awayScore: number): Promise<Game> => {
        const response = await api.put<{ game: Game }>(`/games/${id}/score`, {
            home_score: homeScore,
            away_score: awayScore,
        });
        return response.data.game;
    },

    getCurrentInning: async (id: string): Promise<Inning | null> => {
        try {
            const response = await api.get<{ inning: Inning }>(`/games/${id}/current-inning`);
            return response.data.inning;
        } catch {
            return null;
        }
    },

    getCurrentGameState: async (id: string): Promise<GameState> => {
        const response = await api.get<GameState>(`/analytics/game/${id}/state`);
        return response.data;
    },

    // At-Bat operations
    createAtBat: async (atBatData: Partial<AtBat>): Promise<AtBat> => {
        const response = await api.post<{ atBat: AtBat }>('/at-bats', atBatData);
        return response.data.atBat;
    },

    updateAtBat: async (id: string, atBatData: Partial<AtBat>): Promise<AtBat> => {
        const response = await api.put<{ atBat: AtBat }>(`/at-bats/${id}`, atBatData);
        return response.data.atBat;
    },

    // Pitch operations
    logPitch: async (pitchData: Partial<Pitch>): Promise<Pitch> => {
        const response = await api.post<{ pitch: Pitch }>('/pitches', pitchData);
        return response.data.pitch;
    },

    undoPitch: async (pitchId: string): Promise<{ pitch: Pitch; atBat: AtBat; game: Game }> => {
        const response = await api.delete<{ pitch: Pitch; atBat: AtBat; game: Game }>(`/pitches/${pitchId}`);
        return { pitch: response.data.pitch, atBat: response.data.atBat, game: response.data.game };
    },

    // Result-only edit of the most recent pitch (UX-LG-01 Fix Last Pitch).
    // Throws Error with `.status` and `.code` so the caller can route 409/AB_BOUNDARY
    // to a "use Undo" toast.
    updatePitchResult: async (pitchId: string, pitchResult: PitchResult): Promise<{ pitch: Pitch; atBat: AtBat }> => {
        try {
            const response = await api.patch<{ pitch: Pitch; atBat: AtBat }>(`/pitches/${pitchId}`, {
                pitch_result: pitchResult,
            });
            return { pitch: response.data.pitch, atBat: response.data.atBat };
        } catch (err: unknown) {
            const e = err as { response?: { status?: number; data?: { error?: string; code?: string } } };
            const status = e.response?.status;
            const code = e.response?.data?.code;
            const message = e.response?.data?.error || 'Failed to update pitch';
            const wrapped: Error & { status?: number; code?: string } = new Error(message);
            wrapped.status = status;
            wrapped.code = code;
            throw wrapped;
        }
    },

    // Play operations
    recordPlay: async (playData: Partial<Play>): Promise<Play> => {
        const response = await api.post<{ play: Play }>('/plays', playData);
        return response.data.play;
    },

    // Base runners operations
    updateBaseRunners: async (gameId: string, baseRunners: BaseRunners): Promise<Game> => {
        const response = await api.put<{ game: Game }>(`/games/${gameId}/base-runners`, { base_runners: baseRunners });
        return response.data.game;
    },

    getBaseRunners: async (gameId: string): Promise<BaseRunners> => {
        const response = await api.get<{ base_runners: BaseRunners }>(`/games/${gameId}/base-runners`);
        return response.data.base_runners;
    },

    // Baserunner event operations
    recordBaserunnerEvent: async (eventData: Partial<BaserunnerEvent>): Promise<BaserunnerEvent> => {
        const response = await api.post<{ event: BaserunnerEvent }>('/baserunner-events', eventData);
        return response.data.event;
    },

    toggleHomeAway: async (id: string): Promise<Game> => {
        const response = await api.post<{ game: Game }>(`/games/${id}/toggle-home-away`);
        return response.data.game;
    },

    // End at-bat (sets ab_end_time on server)
    endAtBat: async (
        id: string,
        data: { result: string; outs_after: number; rbi?: number; runs_scored?: number }
    ): Promise<AtBat> => {
        const response = await api.post<{ atBat: AtBat }>(`/at-bats/${id}/end`, data);
        return response.data.atBat;
    },

    getAtBatsByGame: async (gameId: string): Promise<AtBat[]> => {
        const response = await api.get<{ atBats: AtBat[] }>(`/at-bats/game/${gameId}`);
        return response.data.atBats;
    },

    getGamePitches: async (gameId: string): Promise<Pitch[]> => {
        const response = await api.get<{ pitches: Pitch[] }>(`/pitches/game/${gameId}`);
        return response.data.pitches;
    },

    // Bulk backfill of per-pitch velocities for a completed game (manual entry).
    // velocity null clears a value. Returns the number of rows updated.
    updatePitchVelocities: async (
        gameId: string,
        updates: { pitch_id: string; velocity: number | null }[]
    ): Promise<{ updated: number }> => {
        const response = await api.patch<{ updated: number }>(`/pitches/velocities`, { game_id: gameId, updates });
        return response.data;
    },

    // Game Pitcher operations
    getGamePitchers: async (gameId: string): Promise<GamePitcherWithPlayer[]> => {
        const response = await api.get<{ pitchers: GamePitcherWithPlayer[] }>(`/game-pitchers/game/${gameId}`);
        return response.data.pitchers;
    },

    addGamePitcher: async (gameId: string, playerId: string): Promise<GamePitcherWithPlayer> => {
        const response = await api.post<{ pitcher: GamePitcherWithPlayer }>(`/game-pitchers/game/${gameId}`, {
            player_id: playerId,
            pitching_order: 1,
            inning_entered: 1,
        });
        return response.data.pitcher;
    },

    changePitcher: async (gameId: string, playerId: string, inningEntered: number): Promise<GamePitcherWithPlayer> => {
        const response = await api.post<{ pitcher: GamePitcherWithPlayer }>(`/game-pitchers/game/${gameId}/change`, {
            player_id: playerId,
            inning_entered: inningEntered,
        });
        return response.data.pitcher;
    },

    // Opponent Lineup operations
    getOpponentLineup: async (gameId: string): Promise<OpponentLineupPlayer[]> => {
        const response = await api.get<{ lineup: OpponentLineupPlayer[] }>(`/opponent-lineup/game/${gameId}`);
        return response.data.lineup;
    },

    /**
     * Returns the starting lineup from the most recent prior game vs the same
     * opponent team. Empty when there's no linked opponent team or no prior game.
     */
    getLastLineupVsOpponent: async (gameId: string): Promise<OpponentLineupPlayer[]> => {
        const response = await api.get<{ lineup: OpponentLineupPlayer[] }>(`/opponent-lineup/game/${gameId}/last-vs-opponent`);
        return response.data.lineup;
    },

    /**
     * In-game substitute for an opponent batter. Inserts a new lineup row with
     * is_starter=false, inning_entered set, and chains replaced_by_id from the
     * original player. Returns the new sub row.
     */
    substituteOpponentPlayer: async (
        playerId: string,
        sub: { player_name: string; inning_entered: number; position?: string; bats?: 'R' | 'L' | 'S' }
    ): Promise<OpponentLineupPlayer> => {
        const response = await api.post<{ player: OpponentLineupPlayer }>(`/opponent-lineup/player/${playerId}/substitute`, sub);
        return response.data.player;
    },

    createOpponentLineupBulk: async (
        gameId: string,
        players: {
            player_name: string;
            batting_order: number;
            position?: string;
            bats: 'R' | 'L' | 'S';
            is_starter: boolean;
        }[]
    ): Promise<OpponentLineupPlayer[]> => {
        const response = await api.post<{ lineup: OpponentLineupPlayer[] }>(`/opponent-lineup/game/${gameId}/bulk`, {
            players,
        });
        return response.data.lineup;
    },

    // Team pitchers (roster, position='P' only)
    getTeamPitchers: async (teamId: string): Promise<Player[]> => {
        const response = await api.get<{ pitchers: Player[] }>(`/players/pitchers/team/${teamId}`);
        return response.data.pitchers;
    },

    // Full team roster — used by the pitcher selector so position players can pitch
    // in emergencies (parity with mobile, per UX-PB-02).
    getTeamPlayers: async (teamId: string): Promise<Player[]> => {
        const response = await api.get<{ players: Player[] }>(`/players/team/${teamId}`);
        return response.data.players;
    },

    // Pitcher's pitch types
    getPitcherPitchTypes: async (playerId: string): Promise<string[]> => {
        const response = await api.get<{ pitch_types: string[] }>(`/players/${playerId}/pitch-types`);
        return response.data.pitch_types || [];
    },

    getOpponentRoster: async (
        gameId: string
    ): Promise<{
        pitchers: OpponentPitcherProfile[];
        batters: BatterScoutingProfile[];
        players: OpponentRosterPlayer[];
    }> => {
        const response = await api.get<{
            pitchers: OpponentPitcherProfile[];
            batters: BatterScoutingProfile[];
            players?: OpponentRosterPlayer[];
        }>(`/games/${gameId}/opponent-roster`);
        return {
            pitchers: response.data.pitchers ?? [],
            batters: response.data.batters ?? [],
            players: response.data.players ?? [],
        };
    },
};
